import { type LabProfile, type UpdateLabProfileInput, updateLabProfileSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useFormSubmit } from '#client/hooks/useFormSubmit';
import { useFormValues } from '#client/hooks/useFormValues';
import { ENDPOINT } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import {
  CompanyContactCard,
  CompanyIdentityCard,
  CompanyMarkCard,
  CompanyRegistrationCard,
} from './CompanyFields';
import { initialCompanyValues } from './company-form.logic';
import { saveLabProfile } from './settings.service';

/**
 * The lab's own details.
 *
 * Not a `ResourceFormShell`: there is one row, so there is no id, no list and no
 * create. The shell exists to own routing between `/new` and `/:id/edit`, and
 * this screen has neither.
 */
export function CompanyDetailPage() {
  const profile = useApiResource<LabProfile>(ENDPOINT.labProfile);

  if (profile.loading) return <PageLoading title="Company details" />;
  if (profile.error || !profile.data) {
    return (
      <PageMessage title="Company details">
        {profile.error ?? 'Could not load the company details.'}
      </PageMessage>
    );
  }

  // Keyed on the row so the form's initial state comes from a record that is
  // guaranteed present — the same rule ResourceFormShell enforces for the rest
  // of the app.
  return (
    <CompanyForm key={profile.data.updated_at} profile={profile.data} refresh={profile.refresh} />
  );
}

function CompanyForm({ profile, refresh }: { profile: LabProfile; refresh: () => void }) {
  const { values, set } = useFormValues<UpdateLabProfileInput>(initialCompanyValues(profile));

  const form = useFormSubmit<UpdateLabProfileInput>({
    schema: updateLabProfileSchema,
    submit: saveLabProfile,
    successMessage: 'Company details saved',
    onSuccess: refresh,
  });

  return (
    <Page title="Company details" subtitle="Printed on the rate list, slips and test procedures">
      <form onSubmit={form.handleSubmit(values)} noValidate>
        <div className="c-form-stack">
          {form.formError && <Banner tone="critical">{form.formError}</Banner>}

          <Banner tone="info">
            Report templates use these details only when <strong>Use company details</strong> is
            switched on in the template&rsquo;s Header section. The rate list, patient slips and
            printed procedures always use them.
          </Banner>

          <CompanyIdentityCard values={values} onFieldChange={set} errors={form.errors} />
          <CompanyContactCard values={values} onFieldChange={set} errors={form.errors} />
          <CompanyRegistrationCard values={values} onFieldChange={set} errors={form.errors} />
          <CompanyMarkCard
            logoDataUrl={values.logo_data_url}
            onLogoChange={(logoDataUrl) => set('logo_data_url', logoDataUrl)}
          />

          <FormActions
            cancelTo={routes.dashboard}
            submitLabel="Save company details"
            submitting={form.submitting}
          />
        </div>
      </form>
    </Page>
  );
}
